import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthActions } from "@convex-dev/auth/react";
import { Alert, Button, InputWithLabel } from "../../../components/atoms/atoms";
import { useUserStore } from "../Store/userStore";

const ResetPasswordForm = () => {
  const [code, setCode] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const { signIn } = useAuthActions();
  const { showPassword, togglePasswordVisibility, validatePassword } = useUserStore();

  useEffect(() => {
    if (!error) return;
    const timer = setTimeout(() => setError(null), 5000);
    return () => clearTimeout(timer);
  }, [error]);

  useEffect(() => {
    if (!successMessage) return;
    const timer = setTimeout(() => setSuccessMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [successMessage]);

  const handlePasswordChange = (e) => {
    const input = e.target.value;
    setPassword(input);
    setPasswordError(validatePassword(input));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!code) { setError("Please enter the reset code from your email."); return; }
    if (!email) { setError("Please enter your email address."); return; }
    if (passwordError) { setError(passwordError); return; }
    if (!password || password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setIsSubmitting(true);
    try {
      await signIn("password", { flow: "reset-verification", code, email, newPassword: password });
      setSuccessMessage("Password reset successfully!");
      setTimeout(() => navigate("/sign-in"), 2000);
    } catch (err) {
      setError(err?.message || "Reset failed. The code may be invalid or expired.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="lg:container max-w-xl mx-auto">
      {error && <Alert variant="destructive">{error}</Alert>}
      {successMessage && <Alert variant="success">{successMessage}</Alert>}
      <h1 className="text-2xl text-primary font-bold mb-5">Reset Your Password</h1>

      <form onSubmit={handleSubmit}>
        <div className="mb-5">
          <InputWithLabel
            inputId="email"
            labelName="Your Email"
            inputType="email"
            inputPlaceholder="----@-----.com"
            inputRequired
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="mb-5">
          <InputWithLabel
            inputId="code"
            labelName="Reset Code (from your email)"
            inputType="text"
            inputPlaceholder="Enter the code from your email"
            inputRequired
            value={code}
            onChange={(e) => setCode(e.target.value)}
          />
        </div>
        <div className="mb-5">
          <InputWithLabel
            inputId="password"
            labelName="New Password"
            inputType={showPassword ? "text" : "password"}
            inputPlaceholder="**********"
            onChange={handlePasswordChange}
            value={password}
          />
          <div
            className="font-medium py-2 text-sm cursor-pointer"
            onClick={togglePasswordVisibility}
          >
            {showPassword ? "Hide" : "Show"} password
          </div>
          {passwordError && <p className="text-red-500 text-sm mt-2">{passwordError}</p>}
        </div>
        <div className="mb-5">
          <InputWithLabel
            inputId="confirmPassword"
            labelName="Confirm Password"
            inputType={showPassword ? "text" : "password"}
            inputPlaceholder="**********"
            onChange={(e) => setConfirmPassword(e.target.value)}
            value={confirmPassword}
          />
        </div>

        <Button
          className="w-full"
          name={isSubmitting ? "Resetting…" : "Reset Password"}
          disabled={isSubmitting}
        />
      </form>
    </div>
  );
};

export default ResetPasswordForm;
